import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { saveData } from 'services/api/saveData';
import { shakeElement } from 'services/utils/shakeElement';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import DateInput from 'components/forms/basefields/DateInput';
import TimeInput from 'components/forms/basefields/TimeInput';
import Checkbox from 'components/forms/basefields/Checkbox';
import { EmployeeTimeTableDayType, EmployeeTimeTableType, DAYS_OF_WEEK, createDefaultTimetableDays } from './EmployeeTypes';
import '../../style/scss/forms.scss';
import '../../style/scss/modals.scss';

interface EmployeeTimeTableFormProps {
    viewKey?: string;
    employeeId: string;
    timetable?: EmployeeTimeTableType | null;
}

const EmployeeTimeTableForm: React.FC<EmployeeTimeTableFormProps> = ({ employeeId, timetable }) => {
    const { t } = useTranslation();
    const { setFloatingAlert, errorMessages, setErrorMessages } = useGlobalContext();
    const { modalStack, revealModal, closeModal } = useModal();
    const isEditMode = !!timetable;
    const topModal = modalStack[modalStack.length - 1];

    // The form state of the timetable and its day rows
    const [startDate, setStartDate] = useState<string | null>(timetable?.start_date ?? null);
    const [endDate, setEndDate] = useState<string | null>(timetable?.end_date ?? null);
    const [hasSecondWeek, setHasSecondWeek] = useState<boolean>(timetable?.has_second_week ?? false);
    const [week1LeaveHoursBudget, setWeek1LeaveHoursBudget] = useState<string>(timetable?.week1_leave_hours_budget?.toString() ?? '');
    const [week2LeaveHoursBudget, setWeek2LeaveHoursBudget] = useState<string>(timetable?.week2_leave_hours_budget?.toString() ?? '');
    const [days, setDays] = useState<EmployeeTimeTableDayType[]>(timetable?.days ?? createDefaultTimetableDays(false));
    const [buttonLoader, setButtonLoader] = useState(false);

    // Reveal the modal directly and clear old error messages, no initial data needs to be fetched
    useEffect(() => {
        setErrorMessages({});
        revealModal();
        // eslint-disable-next-line
    }, []);

    // Toggle the second week and add the week 2 day rows when missing
    const handleSecondWeekToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        setHasSecondWeek(checked);

        // Add the empty week 2 rows the first time the second week is enabled
        if (checked) {
            setDays(prev => {
                const hasWeek2 = prev.some(day => day.week_number === 2);
                if (hasWeek2) return prev;
                return [...prev, ...DAYS_OF_WEEK.map(day => ({
                    id: null,
                    week_number: 2 as 1 | 2,
                    day_of_week: day.value,
                    start_time: null,
                    end_time: null,
                    break_duration: null,
                    total_hours: null,
                }))];
            });
        }
    };

    // Calculates the net hours from start, end and break, used to prefill the total hours
    const calculateNetHours = (startTime: string | null, endTime: string | null, breakDuration: number | string | null): number | null => {
        if (!startTime || !endTime) return null;

        // Convert both times to minutes
        const [startHour, startMinute] = startTime.split(':').map(Number);
        const [endHour, endMinute] = endTime.split(':').map(Number);
        const startMinutes = startHour * 60 + startMinute;
        const endMinutes = endHour * 60 + endMinute;

        // Ignore invalid or reversed times
        if (isNaN(startMinutes) || isNaN(endMinutes) || endMinutes <= startMinutes) return null;

        // Subtract the break from the gross hours
        const grossHours = (endMinutes - startMinutes) / 60;
        const breakHours = breakDuration ? parseFloat(breakDuration.toString()) : 0;

        return Math.max(grossHours - breakHours, 0);
    };

    // Handle a change to a field of a specific day row
    const handleDayFieldChange = (weekNumber: 1 | 2, dayOfWeek: number, field: keyof EmployeeTimeTableDayType, value: string | null) => {
        setDays(prev => {
            const index = prev.findIndex(day => day.week_number === weekNumber && day.day_of_week === dayOfWeek);
            if (index === -1) return prev;

            // Update the changed field of the day
            const updated = [...prev];
            const updatedDay = { ...updated[index], [field]: value };

            // Prefill the total hours as a convenience, the user can still overwrite it directly
            if (field === 'start_time' || field === 'end_time' || field === 'break_duration') {
                const netHours = calculateNetHours(updatedDay.start_time, updatedDay.end_time, updatedDay.break_duration);
                if (netHours !== null) {
                    updatedDay.total_hours = netHours;
                }
            }

            updated[index] = updatedDay;
            return updated;
        });
    };

    // Submits the timetable to the server
    const handleSubmit = async () => {
        try {
            // Start the button loader and clear old errors
            setButtonLoader(true);
            setErrorMessages({});

            // Build the payload with the day rows of the enabled weeks
            const payload = {
                employee: employeeId,
                start_date: startDate,
                end_date: endDate,
                has_second_week: hasSecondWeek,
                week1_leave_hours_budget: week1LeaveHoursBudget !== '' ? week1LeaveHoursBudget : null,
                week2_leave_hours_budget: hasSecondWeek && week2LeaveHoursBudget !== '' ? week2LeaveHoursBudget : null,
                days: days.filter(day => hasSecondWeek || day.week_number === 1),
            };

            // Patch the existing timetable or post a new one
            const response = isEditMode
                ? await saveData({ apiUrl: `patch_employeetimetable/${timetable!.id}`, method: 'patch', data: payload })
                : await saveData({ apiUrl: 'post_employeetimetable', method: 'post', data: payload });

            // Handle a successful response
            if (response?.status === 200 || response?.status === 201) {
                setButtonLoader(false);
                setFloatingAlert({ type: 'success' });

                // Let the tab refetch the timetables
                if (topModal?.props?.onSuccess) {
                    topModal.props.onSuccess(response.data);
                }

                closeModal();
            }
        } catch (error: any) {
            setButtonLoader(false);

            // Show the error of the server, such as an overlapping period
            const errorData = error?.response?.data;
            if (errorData && typeof errorData === 'object') {
                setErrorMessages(errorData);
            }

            // Shake the modal in any case of an error
            if (topModal?.modalRef) shakeElement(topModal.modalRef.current as HTMLElement);
        }
    };

    // Closes the modal and clears the error messages
    const handleClose = () => {
        setErrorMessages({});
        closeModal();
    };

    // The weeks to render in the form
    const weeksToShow: (1 | 2)[] = hasSecondWeek ? [1, 2] : [1];

    return (
        <div className='employee-timetable-form'>
            <h3 className='header text-center'>
                {t(isEditMode ? 'employee.timetable.edit_timetable_button' : 'employee.timetable.add_timetable_button')}
            </h3>

            {errorMessages.general && (
                <div className='alert form-alert alert-danger' role='alert'>
                    {t(errorMessages.general as string, { defaultValue: errorMessages.general as string })}
                </div>
            )}

            <div className='formset'>
                <div className='field-row'>
                    <div className='formfield'>
                        <label>{t('employee.timetable.start_date_label')}</label>
                        <DateInput
                            id='timetable_start_date'
                            name='timetable_start_date'
                            value={startDate ? new Date(startDate) : null}
                            placeholder={t('employee.timetable.start_date_placeholder') ?? undefined}
                            onChange={setStartDate}
                        />
                    </div>
                    <div className='formfield'>
                        <label>{t('employee.timetable.end_date_label')}</label>
                        <DateInput
                            id='timetable_end_date'
                            name='timetable_end_date'
                            value={endDate ? new Date(endDate) : null}
                            placeholder={t('employee.timetable.end_date_placeholder') ?? undefined}
                            onChange={setEndDate}
                        />
                    </div>
                </div>

                <div className='formfield'>
                    <Checkbox
                        name='has_second_week'
                        label='employee.timetable.has_second_week_label'
                        align='left'
                        value={hasSecondWeek}
                        disabled={isEditMode}
                        onChange={handleSecondWeekToggle}
                    />
                </div>

                {weeksToShow.map(weekNumber => (
                    <div className='timetable-week' key={weekNumber}>
                        {hasSecondWeek && (
                            <div className='week-label'>
                                {t(weekNumber === 1 ? 'employee.timetable.week_1_label' : 'employee.timetable.week_2_label')}
                            </div>
                        )}
                        <div className='timetable-day-row header'>
                            <span></span>
                            <span>{t('employee.timetable.start_time_label')}</span>
                            <span>{t('employee.timetable.end_time_label')}</span>
                            <span>{t('employee.timetable.break_duration_label')}</span>
                            <span>{t('employee.timetable.total_hours_label')}</span>
                        </div>
                        {DAYS_OF_WEEK.map(dayOption => {
                            const day = days.find(d => d.week_number === weekNumber && d.day_of_week === dayOption.value);

                            return (
                                <div className='timetable-day-row' key={`${weekNumber}-${dayOption.value}`}>
                                    <span className='day-label'>{t(dayOption.label)}</span>
                                    <TimeInput
                                        type='time'
                                        id={`start_time_${weekNumber}_${dayOption.value}`}
                                        name={`start_time_${weekNumber}_${dayOption.value}`}
                                        value={day?.start_time ?? undefined}
                                        hideClearButton={true}
                                        onChange={(value) => handleDayFieldChange(weekNumber, dayOption.value, 'start_time', value)}
                                    />
                                    <TimeInput
                                        type='time'
                                        id={`end_time_${weekNumber}_${dayOption.value}`}
                                        name={`end_time_${weekNumber}_${dayOption.value}`}
                                        value={day?.end_time ?? undefined}
                                        hideClearButton={true}
                                        onChange={(value) => handleDayFieldChange(weekNumber, dayOption.value, 'end_time', value)}
                                    />
                                    <input
                                        type='number'
                                        step='0.25'
                                        min='0'
                                        placeholder='0'
                                        value={day?.break_duration ?? ''}
                                        onChange={(e) => handleDayFieldChange(weekNumber, dayOption.value, 'break_duration', e.target.value === '' ? null : e.target.value)}
                                    />
                                    <input
                                        type='number'
                                        step='0.25'
                                        min='0'
                                        placeholder='0'
                                        value={day?.total_hours ?? ''}
                                        onChange={(e) => handleDayFieldChange(weekNumber, dayOption.value, 'total_hours', e.target.value === '' ? null : e.target.value)}
                                    />
                                </div>
                            );
                        })}
                    </div>
                ))}

                <div className='field-row'>
                    <div className='formfield'>
                        <label>{t('employee.timetable.week1_leave_hours_budget_label')}</label>
                        <input
                            type='number'
                            step='0.25'
                            min='0'
                            value={week1LeaveHoursBudget}
                            onChange={(e) => setWeek1LeaveHoursBudget(e.target.value)}
                        />
                    </div>
                    {hasSecondWeek && (
                        <div className='formfield'>
                            <label>{t('employee.timetable.week2_leave_hours_budget_label')}</label>
                            <input
                                type='number'
                                step='0.25'
                                min='0'
                                value={week2LeaveHoursBudget}
                                onChange={(e) => setWeek2LeaveHoursBudget(e.target.value)}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className='footer'>
                <div className='buttons-right'>
                    <SecondaryButton onClick={handleClose} label='general.cancel' size='small' />
                    <PrimaryButton onClick={handleSubmit} label='general.save' size='small' loading={buttonLoader} />
                </div>
            </div>
        </div>
    );
};

export default EmployeeTimeTableForm;
